import { Controller, useForm } from 'react-hook-form'
import React, { FC, useEffect } from 'react'
import styles from './LearnerForm.module.scss'
import { cn } from '@src/utils/cn.ts'
import { ToggleButton } from '@components/toggleButton/toggleButton.tsx'
import { LxTooltip } from '@components/tooltip/tooltip.tsx'
import { LxIcon } from '@components/icon/Icon.tsx'
import { InfoOutlineIcon } from '@icons/utils'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { yupResolver } from '@hookform/resolvers/yup'
import { useFormNotify } from '@components/formNotify/useFormNotify.tsx'
import { isAdmin, Roles } from '@logic/contexts/utils/rolesCheck.ts'
import * as yup from 'yup'
import { useUserContext } from '@logic/contexts/AppStore/UserContext.tsx'
import { LearnerF } from '@logic/contexts/Learners/LearnersFrontend.type.ts'
import dayjs from 'dayjs'
import { LxLoadingSpinner } from '@components/loader/loadingSpinner.tsx'
import LxCancelButton from '@src/components/cancelButton/LxCancelButton'
import LxSaveButton from '@src/components/saveButton/LxSaveButton'
import { useFormPersistence } from '@src/hooks/UseFormPersistence'
import { checkAccSettingsScope } from '@src/utils/accountSettings'
import PrivacyNotice from '@utils/PrivacyNotice'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'

export type LearnerFormType = {
  id?: string
  firstName: string
  lastName: string
  idCode?: string
  nickName?: string
  gender: string
  grade?: string
  birthday: string
  assignLearner?: boolean | null
}
export const defaultLearnerFormValues = (role: Roles): LearnerFormType => ({
  firstName: '',
  lastName: '',
  idCode: '',
  nickName: '',
  gender: '',
  grade: '',
  birthday: '',
  assignLearner: isAdmin(role) ? true : false,
})

export const LearnerFormValidation = yupResolver(
  yup
    .object()
    .shape({
      firstName: yup.string(),
      lastName: yup.string(),
      idCode: yup.string(),
      nickName: yup.string(),
      gender: yup.string(),
      grade: yup.string(),
      birthday: yup.string(),
      assignLearner: yup.boolean(),
    })
    .test('at-least-one', 'At least one of First Name, Last Name, ID Code, or Nick Name is required', (value) => {
      return !!value?.firstName?.trim() || !!value?.lastName?.trim() || !!value?.idCode?.trim() || !!value?.nickName?.trim()
    })
    .required()
)

export const parseLearnerFormToBackend = (formData: LearnerFormType) => ({
  first_name: formData.firstName,
  last_name: formData.lastName,
  id_code: formData.idCode,
  nick_name: formData.nickName,
  gender: formData.gender,
  birthday: formData.birthday,
  grade: formData.grade,
  assign_learner: formData.assignLearner,
})

type Props = {
  onSubmit: (formData: LearnerFormType) => void
  learner?: LearnerF | null
  isEdit?: boolean
  onCancel: () => void
  isSubmitting: boolean
}

export const LearnerForm: FC<Props> = ({ onSubmit, learner, isEdit, onCancel, isSubmitting = false }) => {
  const { currentUser } = useUserContext() as any
  // Add the RBAC check for privacy notice
  const { canAccess: canSeePrivacyMessage, effectiveValue } = checkAccSettingsScope('privacy_notice')
  const accHasPrivacyMessage = effectiveValue === 'true'

  const {
    control,
    handleSubmit,
    reset, 
    formState: { errors, submitCount, isValid },
  } = useForm({
    defaultValues: learner ? { ...learner } : defaultLearnerFormValues(currentUser.userRole),
    resolver: LearnerFormValidation,
  })

  useEffect(() => {
    if (learner) {
      reset({ ...learner })
    }
  }, [learner, reset])

  useFormNotify(errors, submitCount, isValid)
  // const formValues = watch()

  // useFormPersistence({
  //   formId: 'learner',
  //   entityId: learner?.id,
  //   defaultValues: learner ? learner : defaultLearnerFormValues(currentUser.userRole),
  //   formValues,
  //   reset,
  // })

  return (
    <form className={styles.learnerForm} onSubmit={handleSubmit(onSubmit)} role='form'>
      {isSubmitting && (
        <div className={styles.LoadingOverlay}>
          <LxLoadingSpinner />
        </div>
      )}

      <div className={styles.twoColumnGrid}>
        <div className={styles.leftColumn}>
          <Controller
            name='firstName'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label='First Name'
                value={value || ''}
                onChange={onChange}
                error={!!error}
                helperText={error?.message}
                variant='outlined'
                size='medium'
                margin='normal'
                className={styles.muiInputField}
              />
            )}
          />

          <Controller
            name='lastName'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label='Last Name'
                value={value || ''}
                onChange={onChange}
                error={!!error}
                helperText={error?.message}
                variant='outlined'
                size='medium'
                margin='normal'
                className={styles.muiInputField}
              />
            )}
          />

          <Controller
            name='idCode'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label='Identification Code/Number'
                value={value || ''}
                onChange={onChange}
                error={!!error}
                helperText={error?.message}
                variant='outlined'
                size='medium'
                margin='normal'
                className={styles.muiInputField}
              />
            )}
          />

          <Controller
            name='nickName'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label='Nick Name'
                value={value || ''}
                onChange={onChange}
                error={!!error}
                helperText={error?.message}
                variant='outlined'
                size='medium'
                margin='normal'
                className={styles.muiInputField}
              />
            )}
          />
        </div>
        <div className={styles.rightColumn}>
          <Controller
            name='grade'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <TextField
                fullWidth
                label='Grade'
                value={value || ''}
                onChange={onChange}
                error={!!error}
                helperText={error?.message}
                variant='outlined'
                size='medium'
                margin='normal'
                className={styles.muiInputField}
              />
            )}
          />

          <Controller
            name='gender'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              const options = [
                { value: 'male', label: 'Male' },
                { value: 'female', label: 'Female' },
                { value: 'others', label: 'Other' },
              ]

              const selectedOption = options.find((opt) => opt.value === value) || null

              return (
                <Autocomplete
                  fullWidth
                  options={options}
                  getOptionLabel={(option) => option.label}
                  value={selectedOption}
                  onChange={(_, newValue) => onChange(newValue?.value || '')}
                  renderInput={(params) => (
                    <TextField {...params} label='Gender' error={!!error} helperText={error?.message} variant='outlined' size='medium' margin='normal' className={styles.muiInputField} />
                  )}
                  isOptionEqualToValue={(option, val) => option.value === val?.value}
                />
              )
            }}
          />

          <Controller
            name='birthday'
            control={control}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format='MMM DD, YYYY'
                  className={cn(styles.datePicker, styles.muiInputField)}
                  onChange={(e) => onChange(e?.format('YYYY-MM-DD') || null)}
                  value={value ? dayjs(value) : null}
                  slotProps={{
                    textField: {
                      inputProps: {
                        'aria-label': 'Select your birth date in Month Day, Year format',
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            )}
          />

          {isAdmin(currentUser.userRole) && !isEdit && (
            <Controller
              name='assignLearner'
              control={control}
              render={({ field: { onChange, value } }) => (
                <div className={styles.assignLearnerSection}>
                  <ToggleButton
                    value={value}
                    onChange={onChange}
                    ariaLabel='Assign this learner to me checkbox (On the tablets, you can run activities only for the learners who are assigned to your profile.
                   You can assign/unassign learners to your profile at any time.)'
                  />
                  <span>Assign this learner to me</span>
                  <LxTooltip
                    tooltipText={`On the tablets, you can run activities only for the learners who are assigned to your profile.
                   You can assign/unassign learners to your profile at any time.`}
                  >
                    <LxIcon icon={InfoOutlineIcon} />
                  </LxTooltip>
                </div>
              )}
            />
          )}
        </div>
      </div>

      <div className={styles.dialogActions}>
        <LxCancelButton onCancel={onCancel} classNames='lxActionButton, lxActionButtonDefaultSize' />
        <LxSaveButton onSubmit={handleSubmit(onSubmit)} label={isEdit ? 'Save Changes' : 'Create Learner'} classNames='lxActionButton,lxActionButtonFilled,lxActionButtonDefaultSize' />
      </div>

      {accHasPrivacyMessage && canSeePrivacyMessage && <PrivacyNotice show={accHasPrivacyMessage && canSeePrivacyMessage} className={styles.privacyNotice} titleId='privacy-dialog-title' />}
    </form>
  )
}
